/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useRef, useState, type KeyboardEvent } from 'react';
import { Archive, CalendarDays, Check, CircleAlert, Info, Pencil, Plus, RotateCcw, TriangleAlert, X } from 'lucide-react';
import {
  DAY_COLLECTION_MAX,
  DAY_KIND_LABEL,
  DAY_OUTFITS_OFF_TEXT,
  DAY_RULE_KINDS,
  DAY_STORE_UNREADABLE_TEXT,
  DAY_TABS,
  NTH_CHOICES,
  attentionRows,
  bannerWarnings,
  collectionRefusal,
  dayInputsOf,
  dayOutfitEdit,
  dayOutfitsSwitchedOn,
  dayRows,
  dayRuleFromInputs,
  dayTabCounts,
  dayWarningText,
  distributionText,
  firstDayTab,
  greetingLength,
  greetingRefusal,
  monthDayCount,
  nextDatesText,
  outfitNameOf,
  ratingLabel,
  ratingShares,
  ratingText,
  retiredRows,
  type AttentionRow,
  type DayDetails,
  type DayInputs,
  type DayRow,
  type DayTab,
} from '../lib/dayOutfits';
import {
  MASCOT_LIMITS,
  attentionRetireConfirmText,
  formatTime,
  outfitPicture,
  pickCountText,
  picksFor,
  retireDayConfirmText,
  type AdminOutfit,
  type DesktopMascotCatalog,
  type DayWarning,
  type KeptOutfit,
  type OutfitRating,
} from '../lib/desktopMascot';
import { DAY_GREETING_MAX, DAY_MAX_EXPLICIT_YEARS, DAY_MAX_SPAN, MONTH_NAMES, WEEKDAY_NAMES, describeDayRule, type DayRule } from '../lib/mascotDays';
import { Button, Callout, Checkbox, Field, Icon, Input, NumberInput, Panel, Select, StatusChip } from '../ui';
import { SaveNote } from './SaveNote';
import { clockNow, wallpaperClass, type Wallpaper } from './mascotUi';

/** A note the card shows in this section: under the outfit it is about, else at the top. */
export interface DayOutfitsNote {
  ok: boolean;
  text: string;
  outfitId?: string;
}

/**
 * The writes the card makes for this section, with its lock, its notes and the catalog read again after a refusal
 * (DesktopMascotCard.tsx): Edit's Save (a rename with PATCH, the day with PUT …/day, or both), Retire after the admin
 * confirms `question`, and Restore. Save answers whether the change was saved (the form then closes).
 */
export interface DayOutfitsWrites {
  save: (outfit: Pick<AdminOutfit, 'id' | 'name'>, change: { name?: string; details?: DayDetails }) => Promise<boolean>;
  retire: (outfit: { id: string; name: string }, question: string) => Promise<void>;
  restore: (outfit: { id: string; name: string }) => Promise<void>;
  /** A refusal worked out before anything is sent (the form's fields), shown like the service's. */
  refuse: (outfitId: string, text: string) => void;
}

const SEVERITY_LABEL: Record<DayWarning['severity'], string> = { error: 'Error', warning: 'Warning', info: 'Note' };

function SeverityIcon({ severity }: { severity: DayWarning['severity'] }) {
  const label = SEVERITY_LABEL[severity];
  if (severity === 'error') return <Icon icon={CircleAlert} size="sm" label={label} className="ui-warning-icon is-error" />;
  if (severity === 'warning') return <Icon icon={TriangleAlert} size="sm" label={label} className="ui-warning-icon is-warning" />;
  return <Icon icon={Info} size="sm" label={label} className="ui-warning-icon is-info" />;
}

/**
 * The day fields of the Add form and of Edit: when it comes (a rule kind and that kind's own fields), the rule in words
 * with its next three dates by the Pacific calendar, worked out live; the greeting with its 140-character counter (as
 * the service counts: an emoji is 1 or more, a joined one several); holiday or fun day; and the optional collection.
 * A refused greeting or collection is marked invalid, with its reason as the field's description.
 */
export function DayRuleFields({ idPrefix, value, now, disabled, onChange }: {
  idPrefix: string;
  value: DayInputs;
  now: number;
  disabled: boolean;
  onChange: (value: DayInputs) => void;
}) {
  const set = (patch: Partial<DayInputs>) => onChange({ ...value, ...patch });
  const rule = dayRuleFromInputs(value);
  const greetingProblem = value.greeting.trim() ? greetingRefusal(value.greeting) : null;
  const collectionProblem = collectionRefusal(value.collection);
  const monthSelect = (label: string) => (
    <Select
      size="sm"
      aria-label={label}
      className="ui-w-auto"
      value={value.month}
      onChange={(e) => {
        const month = Number(e.target.value);
        set({ month, day: Math.min(value.day, monthDayCount(month)) });
      }}
    >
      {MONTH_NAMES.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
    </Select>
  );
  const spanField = (
    <div className="ui-actions">
      <span>Lasts</span>
      <NumberInput
        size="sm"
        type="number"
        min={1}
        max={DAY_MAX_SPAN}
        step={1}
        aria-label="How many days it lasts"
        className="ui-w-xs"
        value={value.spanDays}
        onChange={(e) => set({ spanDays: e.target.value })}
      />
      <span>day{value.spanDays.trim() === '1' ? '' : 's'} (1 to {DAY_MAX_SPAN})</span>
    </div>
  );
  return (
    <fieldset disabled={disabled} className="ui-fieldset">
      <legend className="ui-field-label">Its day</legend>
      <Field label="When it comes" labelHidden>
        <Select id={`${idPrefix}-rule`} aria-label="When it comes" value={value.rule} onChange={(e) => set({ rule: e.target.value as DayRule['kind'] })}>
          {DAY_RULE_KINDS.map(({ kind, label }) => <option key={kind} value={kind}>{label}</option>)}
        </Select>
      </Field>
      {value.rule === 'fixed' && (
        <div className="ui-actions">
          {monthSelect('Month')}
          <Select size="sm" aria-label="Day of the month" className="ui-w-auto" value={value.day} onChange={(e) => set({ day: Number(e.target.value) })}>
            {Array.from({ length: monthDayCount(value.month) }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}
          </Select>
        </div>
      )}
      {value.rule === 'nth-weekday' && (
        <div className="ui-actions">
          <Select size="sm" aria-label="Which one in the month" className="ui-w-auto" value={value.n} onChange={(e) => set({ n: Number(e.target.value) })}>
            {NTH_CHOICES.map(({ n, label }) => <option key={n} value={n}>{label}</option>)}
          </Select>
          <Select size="sm" aria-label="Weekday" className="ui-w-auto" value={value.weekday} onChange={(e) => set({ weekday: Number(e.target.value) })}>
            {WEEKDAY_NAMES.map((name, index) => <option key={name} value={index}>{name}</option>)}
          </Select>
          <span>of</span>
          {monthSelect('Month')}
        </div>
      )}
      {value.rule === 'easter' && (
        <Field label="Days from Easter Sunday" labelHidden help="Below 0 is before Easter (Mardi Gras is -47, Good Friday -2); 0 is Easter Sunday itself.">
          <div className="ui-actions">
            <NumberInput
              size="sm"
              type="number"
              min={-70}
              max={70}
              step={1}
              aria-label="Days from Easter Sunday"
              className="ui-w-xs"
              value={value.easterOffset}
              onChange={(e) => set({ easterOffset: e.target.value })}
            />
            <span>days from Easter Sunday</span>
          </div>
        </Field>
      )}
      {value.rule === 'explicit' && (
        <div className="ui-form">
          <span className="ui-cell-sub">Its first day in each year: IT enters next year&apos;s when it is announced (Hanukkah, Lunar New Year, Super Bowl Sunday).</span>
          {value.dates.map((date, index) => (
            <div key={index} className="ui-actions">
              <Input
                size="sm"
                type="date"
                min="2020-01-01"
                max="2100-12-31"
                aria-label={`First day, date ${index + 1}`}
                className="ui-w-auto"
                value={date}
                onChange={(e) => set({ dates: value.dates.map((item, at) => (at === index ? e.target.value : item)) })}
              />
              <Button variant="ghost" size="sm" icon={X} aria-label={`Take out date ${index + 1}`} onClick={() => set({ dates: value.dates.filter((_, at) => at !== index) })} />
            </div>
          ))}
          <div className="ui-actions">
            <Button size="sm" icon={Plus} disabled={value.dates.length >= DAY_MAX_EXPLICIT_YEARS} onClick={() => set({ dates: [...value.dates, ''] })}>Add a year</Button>
          </div>
          {spanField}
          <Checkbox checked={value.confirmEachYear} onChange={(e) => set({ confirmEachYear: e.target.checked })} label="Confirm the date each year (it is announced later; the console reminds you)" />
        </div>
      )}
      {value.rule === 'once' && (
        <div className="ui-form">
          <Input
            size="sm"
            type="date"
            min="2020-01-01"
            max="2100-12-31"
            aria-label="Its first day"
            className="ui-w-auto"
            value={value.date}
            onChange={(e) => set({ date: e.target.value })}
          />
          {spanField}
        </div>
      )}
      <p className="ui-rule-line" aria-live="polite">
        {rule.ok
          ? <>{`${describeDayRule(rule.rule)}. `}<span className="ui-quiet">{nextDatesText(rule.rule, now)}</span></>
          : <span className="ui-text-warn">{rule.message}</span>}
      </p>
      <Field label="Greeting">
        <Input
          id={`${idPrefix}-greeting`}
          aria-describedby={`${idPrefix}-greeting-hint`}
          invalid={greetingProblem ? true : undefined}
          value={value.greeting}
          placeholder="Happy Pi Day! Want a slice of help?"
          onChange={(e) => set({ greeting: e.target.value })}
        />
        <p id={`${idPrefix}-greeting-hint`} className={greetingProblem ? 'ui-field-note is-error' : 'ui-field-note'}>
          {greetingProblem ?? 'People see it all day in place of “What can we work on?”, as plain text; emoji are welcome.'}
          {' '}{greetingLength(value.greeting)} / {DAY_GREETING_MAX} characters.
        </p>
      </Field>
      <div className="ui-actions" role="radiogroup" aria-label="Kind">
        {(['holiday', 'fun'] as const).map((kind) => (
          <label key={kind} className="ui-check">
            <input type="radio" name={`${idPrefix}-kind`} checked={value.kind === kind} onChange={() => set({ kind })} />
            <span>{DAY_KIND_LABEL[kind]}</span>
          </label>
        ))}
      </div>
      <Field label="Collection (optional)">
        <Input
          id={`${idPrefix}-collection`}
          aria-describedby={`${idPrefix}-collection-hint`}
          invalid={collectionProblem ? true : undefined}
          maxLength={DAY_COLLECTION_MAX}
          value={value.collection}
          placeholder="Core collection"
          onChange={(e) => set({ collection: e.target.value })}
        />
        <p id={`${idPrefix}-collection-hint`} className={collectionProblem ? 'ui-field-note is-error' : 'ui-field-note'}>
          {collectionProblem ?? 'Groups day outfits here and, later, in the costume shop.'}
        </p>
      </Field>
    </fieldset>
  );
}

/**
 * An outfit's picture in a row: the gallery's thumbnail (inline, or by its route for an outfit kept beyond the catalog), or
 * the stored image (retired and unfinished ones); a route is loaded lazily.
 */
function RowPicture({ picture, wallpaper }: { picture: { thumbnailPng: string } | { imageUrl: string }; wallpaper: Wallpaper }) {
  return (
    <span className={`ui-row-pic ${wallpaperClass(wallpaper)}`}>
      {'thumbnailPng' in picture
        ? <img src={`data:image/png;base64,${picture.thumbnailPng}`} alt="" />
        : <img src={picture.imageUrl} loading="lazy" decoding="async" alt="" width={46} height={46} />}
    </span>
  );
}

/**
 * An outfit's stars: "★ 4.3 (12)" and a five-column bar of how many gave 1 to 5 stars (one series, so no legend; each
 * column names its count on hover, and the whole is read out as words). A screen reader hears "Rated 4.3 out of 5 by 12
 * people" (an image with that name), never the star sign. Nothing while the ratings cannot be read.
 */
export function RatingLine({ rating }: { rating: OutfitRating | undefined }) {
  const text = ratingText(rating);
  if (!rating || text === null) return null;
  const shares = ratingShares(rating);
  const tallest = Math.max(...shares);
  const words = distributionText(rating);
  const counts = words.split(' · ').reverse();
  const label = ratingLabel(rating) ?? text;
  return (
    <span className="ui-rating">
      <span role="img" aria-label={label} title={label}>{text}</span>
      {rating.count > 0 && (
        <span className="ui-rating-bars">
          <span role="img" aria-label={`Stars given, 1 to 5: ${words}`} title={words} className="ui-rating-cols">
            {shares.map((share, index) => (
              <span
                key={index}
                data-stars={index + 1}
                title={counts[index]}
                className="ui-rating-col"
                // A computed height: the share of this column, as the kit's meters compute their width.
                style={{ height: `${tallest > 0 ? Math.round((share / tallest) * 100) : 0}%` }}
              />
            ))}
          </span>
          <span className="ui-rating-scale" aria-hidden="true">1–5★</span>
        </span>
      )}
    </span>
  );
}

/** A day outfit's row: its picture, name, kind and badges, its rule and greeting, its next dates, picks and stars, and its buttons. */
function DayOutfitRow({ row, wallpaper, picks, now, switchedOn, locked, editing, pending, onEdit, onRetire }: {
  row: DayRow;
  wallpaper: Wallpaper;
  picks: Readonly<Record<string, number>>;
  now: number;
  /** False while the service has day outfits switched off: nobody gets one, today's included. */
  switchedOn: boolean;
  locked: boolean;
  /** Its Edit form is open. */
  editing: boolean;
  pending: 'save' | 'retire' | 'restore' | null;
  onEdit: () => void;
  onRetire: () => void;
}) {
  const { outfit, day } = row;
  return (
    <div className="ui-outfit-row">
      <RowPicture picture={outfitPicture(outfit)} wallpaper={wallpaper} />
      <div className="ui-outfit-row-text">
        <div className="ui-cell-title">
          <strong>{outfit.name}</strong>
          <StatusChip tone={day.kind === 'holiday' ? 'accent' : 'info'}>{DAY_KIND_LABEL[day.kind]}</StatusChip>
          {row.swappedIn && <StatusChip tone="ok">Swapped in today (Pacific)</StatusChip>}
          {row.state === 'today' && !row.swappedIn && (
            <StatusChip tone="neutral">{switchedOn ? 'In the gallery today (another is swapped in)' : 'Its day is today, but day outfits are switched off'}</StatusChip>
          )}
          {row.showingSomewhereNow && row.state !== 'today' && <StatusChip tone="neutral">On somewhere on Earth now</StatusChip>}
          {day.collection && <span className="ui-cell-sub">{day.collection}</span>}
        </div>
        <span>{day.ruleText}</span>
        <span>{`“${day.greeting}”`}</span>
        <span className="ui-cell-sub">{nextDatesText(day.rule, now)}</span>
        <span className="ui-cell-sub">
          {pickCountText(picksFor(picks, outfit.id))} · {outfit.width} x {outfit.height} px · added by {outfit.addedBy} on {formatTime(outfit.addedAtMs)}
        </span>
        <RatingLine rating={outfit.rating} />
      </div>
      <div className="ui-row-actions">
        <Button size="sm" icon={Pencil} disabled={locked} aria-expanded={editing} onClick={onEdit}>Edit</Button>
        <Button variant="danger" size="sm" icon={Archive} busy={pending === 'retire'} disabled={locked} onClick={onRetire}>Retire</Button>
      </div>
    </div>
  );
}

/** A retired outfit's row: what it was, who retired it and when, the stars it kept, and Restore. */
function RetiredRow({ outfit, wallpaper, locked, pending, onRestore }: {
  outfit: KeptOutfit;
  wallpaper: Wallpaper;
  locked: boolean;
  pending: boolean;
  onRestore: () => void;
}) {
  return (
    <div className="ui-outfit-row">
      <RowPicture picture={{ imageUrl: outfit.imageUrl }} wallpaper={wallpaper} />
      <div className="ui-outfit-row-text">
        <div className="ui-cell-title">
          <strong>{outfit.name}</strong>
          <StatusChip tone={outfit.kind === 'holiday' ? 'accent' : outfit.kind === 'fun' ? 'info' : 'neutral'}>
            {outfit.kind ? DAY_KIND_LABEL[outfit.kind] : 'Gallery outfit'}
          </StatusChip>
        </div>
        {outfit.ruleText && <span>{outfit.ruleText}</span>}
        <span className="ui-cell-sub">
          Retired by {outfit.updatedBy} on {formatTime(outfit.updatedAtMs)} · {outfit.width} x {outfit.height} px · added by {outfit.addedBy} on {formatTime(outfit.addedAtMs)}
        </span>
        <RatingLine rating={outfit.rating} />
      </div>
      <div className="ui-row-actions">
        <Button size="sm" icon={RotateCcw} busy={pending} disabled={locked} onClick={onRestore}>Restore</Button>
      </div>
    </div>
  );
}

/** A row of "Needs attention": what happened, and the buttons that fix it. */
function AttentionItem({ row, wallpaper, locked, pending, onRestore, onRetire }: {
  row: AttentionRow;
  wallpaper: Wallpaper;
  locked: boolean;
  pending: 'save' | 'retire' | 'restore' | null;
  onRestore: () => void;
  onRetire: () => void;
}) {
  return (
    <div className="ui-outfit-row">
      <RowPicture picture={row.picture} wallpaper={wallpaper} />
      <div className="ui-outfit-row-text">
        <strong>{row.name}</strong>
        <span>{row.text}</span>
      </div>
      <div className="ui-row-actions">
        {row.canRestore && (
          <Button size="sm" icon={RotateCcw} busy={pending === 'restore'} disabled={locked} onClick={onRestore}>Restore</Button>
        )}
        <Button variant="danger" size="sm" icon={Archive} busy={pending === 'retire'} disabled={locked} onClick={onRetire}>{row.retireLabel}</Button>
      </div>
    </div>
  );
}

/**
 * Outfits and mascot › Gallery › Day outfits: every day outfit by tab (Today, Scheduled by next date, Needs a date,
 * Past) by the Pacific calendar, the retired outfits (Retired, any outfit: Retire keeps every one for the costume
 * shop), and above them the warnings and the outfits that need attention. Edit changes a day outfit's name (PATCH, never
 * a window), rule, greeting, kind and collection (PUT …/day); Retire and Restore keep the same id. An auditor sees it
 * all with every button disabled. The card makes the writes (DayOutfitsWrites) and passes their notes back.
 */
export function DayOutfitsSection({ catalog, now, wallpaper, locked, note, writes }: {
  catalog: DesktopMascotCatalog;
  now: number;
  /** The wallpaper the card's previews sit on, for the pictures here. */
  wallpaper: Wallpaper;
  /** Nothing can change now: an auditor, a write in flight, or no catalog. */
  locked: boolean;
  note: DayOutfitsNote | null;
  writes: DayOutfitsWrites;
}) {
  const [chosenTab, setChosenTab] = useState<DayTab | null>(null);
  const [editing, setEditing] = useState<{ id: string; name: string; day: DayInputs } | null>(null);
  const [pending, setPending] = useState<{ id: string; action: 'save' | 'retire' | 'restore' } | null>(null);
  const tabRefs = useRef(new Map<DayTab, HTMLButtonElement>());

  const unreadable = catalog.annexRevision === null;
  const rows = dayRows(catalog, now);
  const retired = retiredRows(catalog);
  const counts = dayTabCounts(rows, catalog);
  const tab = chosenTab ?? firstDayTab(rows);
  const nameOf = outfitNameOf(catalog);
  const warnings = bannerWarnings(catalog.dayWarnings ?? []);
  const attention = attentionRows(catalog);
  const writeLocked = locked || unreadable;
  const shownDays = tab === 'retired' ? [] : rows[tab];
  const pendingFor = (id: string) => (pending?.id === id ? pending.action : null);
  // A note about an outfit shows under it: in "Needs attention" first, else in the tab shown; anywhere else (it moved
  // to another tab, or left the section) it shows at the top.
  const attentionIds = new Set(attention.map((row) => row.id));
  const tabIds = new Set(tab === 'retired' ? retired.map((outfit) => outfit.id) : shownDays.map((row) => row.outfit.id));
  const attentionNote = (id: string) => (note?.outfitId === id ? note : null);
  const rowNote = (id: string) => (note?.outfitId === id && !attentionIds.has(id) ? note : null);
  const topNote = note && (note.outfitId === undefined || (!attentionIds.has(note.outfitId) && !tabIds.has(note.outfitId))) ? note : null;

  const run = async (id: string, action: 'save' | 'retire' | 'restore', write: () => Promise<unknown>) => {
    setPending({ id, action });
    try {
      await write();
    } finally {
      setPending(null);
    }
  };

  const save = (row: DayRow) => {
    if (!editing || editing.id !== row.outfit.id) return;
    const change = dayOutfitEdit(row.outfit, row.day, { name: editing.name, day: editing.day });
    if (change.kind === 'unchanged') {
      setEditing(null);
      return;
    }
    if (change.kind === 'refused') {
      writes.refuse(row.outfit.id, change.message);
      return;
    }
    void run(row.outfit.id, 'save', async () => {
      if (await writes.save(row.outfit, { name: change.name, details: change.details })) setEditing(null);
    });
  };

  const retire = (outfit: { id: string; name: string }, question: string) => void run(outfit.id, 'retire', () => writes.retire(outfit, question));
  const restore = (outfit: { id: string; name: string }) => void run(outfit.id, 'restore', () => writes.restore(outfit));

  const editor = (row: DayRow) => editing && editing.id === row.outfit.id && (
    <div className="ui-row-editor">
      <div className="ui-form">
        <Field label="Name">
          <Input
            id="mascot-day-edit-name"
            maxLength={MASCOT_LIMITS.nameMax}
            value={editing.name}
            disabled={writeLocked}
            onChange={(e) => {
              const value = e.target.value;
              setEditing((current) => current && { ...current, name: value });
            }}
          />
        </Field>
        <DayRuleFields
          idPrefix="mascot-day-edit"
          value={editing.day}
          now={now}
          disabled={writeLocked}
          onChange={(value) => setEditing((current) => current && { ...current, day: value })}
        />
        <div className="ui-actions">
          <Button variant="primary" size="sm" icon={Check} busy={pendingFor(row.outfit.id) === 'save'} disabled={writeLocked} onClick={() => save(row)}>Save</Button>
          <Button variant="ghost" size="sm" icon={X} disabled={pendingFor(row.outfit.id) === 'save'} onClick={() => setEditing(null)}>Cancel</Button>
        </div>
      </div>
    </div>
  );

  const empty = DAY_TABS.find((item) => item.tab === tab)?.empty ?? '';
  const switchedOn = dayOutfitsSwitchedOn(catalog);

  /** The tabs' keyboard: Left and Right move to the tab before or after (round), Home and End to the first or last. */
  const onTabKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    const at = DAY_TABS.findIndex((item) => item.tab === tab);
    const next = event.key === 'ArrowRight' ? (at + 1) % DAY_TABS.length
      : event.key === 'ArrowLeft' ? (at - 1 + DAY_TABS.length) % DAY_TABS.length
        : event.key === 'Home' ? 0
          : event.key === 'End' ? DAY_TABS.length - 1
            : -1;
    if (next < 0) return;
    event.preventDefault();
    const key = DAY_TABS[next]!.tab;
    setChosenTab(key);
    tabRefs.current.get(key)?.focus();
  };

  return (
    <Panel
      heading="h3"
      titleId="mascot-days-title"
      title={<span className="ui-title-icon"><Icon icon={CalendarDays} size="md" />Day outfits</span>}
      description="Holiday and fun outfits Gyro wears for everyone on their day, with their greeting; and every retired outfit, ready to restore"
      footer={(
        <span>
          A person&apos;s day runs midnight to midnight in their own time zone (Pacific where an app sends none); they can still pick
          another outfit that day, and afterwards they are back on their own pick. When two share a date, the shorter one is
          swapped in, then the one added first (a restored outfit keeps its place); the other is in the gallery that day. Dates
          here are by the Pacific calendar. Add one under “Add an outfit” with “Day outfit”. Retired outfits of every kind are
          kept here, ready to restore.
        </span>
      )}
    >
      {unreadable ? (
        <Callout tone="warn" role="alert">{DAY_STORE_UNREADABLE_TEXT}</Callout>
      ) : (
        <div className="ui-form">
          {!switchedOn && (
            <p className="ui-note is-warn" role="status"><Icon icon={TriangleAlert} size="sm" /><span>{DAY_OUTFITS_OFF_TEXT}</span></p>
          )}
          <SaveNote note={topNote} />
          {warnings.length > 0 && (
            <div role="region" aria-label="Day outfit warnings" className="ui-callout ui-callout--neutral">
              <ul className="ui-warnings">
                {warnings.map((warning, index) => (
                  <li key={`${warning.kind}-${index}`} className={`ui-warning is-${warning.severity}`}>
                    <SeverityIcon severity={warning.severity} />
                    <span>{dayWarningText(warning, nameOf)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {attention.length > 0 && (
            <div role="region" aria-labelledby="mascot-days-attention" className="ui-callout ui-callout--warn ui-attention">
              <div id="mascot-days-attention" className="ui-callout-title ui-title-icon">
                <Icon icon={TriangleAlert} size="sm" /> {`Needs attention (${attention.length})`}
              </div>
              {attention.map((row) => (
                <div key={row.id} className="ui-outfit-rows-item">
                  <AttentionItem
                    row={row}
                    wallpaper={wallpaper}
                    locked={writeLocked}
                    pending={pendingFor(row.id)}
                    onRestore={() => restore(row)}
                    onRetire={() => retire(row, attentionRetireConfirmText(row, catalog.picks))}
                  />
                  <SaveNote note={attentionNote(row.id)} />
                </div>
              ))}
            </div>
          )}
          <div role="tablist" aria-label="Day outfits by date, and retired outfits" className="ui-tabbar">
            {DAY_TABS.map(({ tab: key, label }) => (
              <button
                key={key}
                ref={(element) => {
                  if (element) tabRefs.current.set(key, element);
                  else tabRefs.current.delete(key);
                }}
                type="button"
                role="tab"
                id={`mascot-days-tab-${key}`}
                aria-selected={tab === key}
                aria-controls="mascot-days-panel"
                tabIndex={tab === key ? 0 : -1}
                className={tab === key ? 'ui-tab is-active' : 'ui-tab'}
                onClick={() => setChosenTab(key)}
                onKeyDown={onTabKey}
              >
                {`${label} (${counts[key]})`}
              </button>
            ))}
          </div>
          <div role="tabpanel" id="mascot-days-panel" tabIndex={0} aria-labelledby={`mascot-days-tab-${tab}`} className="ui-outfit-rows">
            {tab === 'retired' && retired.length > 0 && (
              <p className="ui-cell-sub">
                Every retired outfit, day outfits and gallery outfits alike: kept with its image and ratings for the costume shop.
                Restore brings it back under the same id (a gallery outfit with the window it had).
              </p>
            )}
            {(tab === 'retired' ? retired.length : shownDays.length) === 0 && <p className="ui-cell-sub">{empty}</p>}
            {shownDays.map((row) => (
              <div key={row.outfit.id} className="ui-outfit-rows-item">
                <DayOutfitRow
                  row={row}
                  wallpaper={wallpaper}
                  picks={catalog.picks}
                  now={now}
                  switchedOn={switchedOn}
                  locked={writeLocked}
                  editing={editing?.id === row.outfit.id}
                  pending={pendingFor(row.outfit.id)}
                  onEdit={() => setEditing(editing?.id === row.outfit.id ? null : { id: row.outfit.id, name: row.outfit.name, day: dayInputsOf(row.day, clockNow()) })}
                  onRetire={() => retire(row.outfit, retireDayConfirmText(row.outfit, catalog.picks))}
                />
                {editor(row)}
                <SaveNote note={rowNote(row.outfit.id)} />
              </div>
            ))}
            {tab === 'retired' && retired.map((outfit) => (
              <div key={outfit.id} className="ui-outfit-rows-item">
                <RetiredRow outfit={outfit} wallpaper={wallpaper} locked={writeLocked} pending={pendingFor(outfit.id) === 'restore'} onRestore={() => restore(outfit)} />
                <SaveNote note={rowNote(outfit.id)} />
              </div>
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}
